/**
 * donorPerformance.js — the donor-experience performance instrument (CON-63).
 *
 * Two event names, one transport, no donor-visible behaviour:
 *
 *   donor_perf:web_vital   — LCP / INP / CLS / TTFB / FCP from the `web-vitals`
 *                            library, one event per metric per page.
 *   donor_perf:journey_mark — the five donor-journey moments SPEC §2 names.
 *
 * Both ride `captureClientEvent` (./client.js), which scrubs sensitive keys and
 * stamps `app_name` / `event_version` / `client_timestamp`. PostHog's own
 * `$web_vitals` autocapture stays OFF in posthog-config.js on purpose — it
 * carries none of the dimensions below.
 *
 * Dimensions on every event: `surface`, `connection`, `device_memory`,
 * `in_iframe`, and — for an embedded surface only — `host_hostname`. The host
 * is recorded as a HOSTNAME and never the referrer URL: a nonprofit's page path
 * can identify a campaign or a donor.
 *
 * Marks stamped before `initDonorPerformance` runs are buffered (the wiring
 * runs on an idle callback after first render, and `form_visible` can beat it)
 * and flushed once the PostHog client is known. The buffer is bounded, so a
 * surface that never initialises analytics leaks nothing.
 */

import { onCLS, onFCP, onINP, onLCP, onTTFB } from 'web-vitals';

import { captureClientEvent } from './client.js';

export const WEB_VITAL_EVENT = 'donor_perf:web_vital';
export const JOURNEY_MARK_EVENT = 'donor_perf:journey_mark';
/**
 * CON-86 / PE-2140. A donor tapped Donate and the preloaded checkout could not
 * take it. Deliberately NOT a journey mark: `JOURNEY_MARKS` is the closed set of
 * five moments SPEC §2 owns, and this is a failure, not a moment on the way to
 * paying. Keeping it a separate event also keeps the journey funnel honest — a
 * lost tap must not read as a `checkout_requested` that simply took a while.
 */
export const CHECKOUT_TAP_LOST_EVENT = 'donor_perf:checkout_tap_lost';
/**
 * CON-73. The hosted page's last-resort reveal timer fired: the form never
 * reported ready, so the page revealed itself. Not a journey mark, for the same
 * reason as the lost tap — it is a failure, and it must not read as a moment on
 * the way to paying. A PostHog event rather than a console line because the
 * Sentry console integration captures `error` only, so a warn is uncountable.
 */
export const REVEAL_FALLBACK_EVENT = 'donor_perf:reveal_fallback';

/** The five donor-journey moments. SPEC §2 is the source of truth for this list. */
export const JOURNEY_MARKS = Object.freeze({
  FORM_VISIBLE: 'form_visible',
  CHECKOUT_REQUESTED: 'checkout_requested',
  CHECKOUT_VISIBLE: 'checkout_visible',
  WALLET_READY: 'wallet_ready',
  PAYMENT_ROUND_TRIP: 'payment_round_trip',
});

const KNOWN_MARKS = new Set(Object.values(JOURNEY_MARKS));

const PERFORMANCE_MARK_PREFIX = 'cs:';

/**
 * Marks — and the one pre-init failure event, `markRevealFallback` — that
 * arrived before init. Bounded — there are only five mark names.
 */
const MAX_BUFFERED_MARKS = 10;
let bufferedMarks = [];

/**
 * Marks that mean "the first time this page reached the moment". A component
 * anchor can remount inside one page load (the checkout's payment step
 * unmounts on Back and remounts on Continue; the express-wallet block unmounts
 * when a promo zeroes the total), so a per-instance ref is not a per-page
 * guard. This set is. `payment_round_trip` and `checkout_requested` are NOT
 * in it — a retry after a decline is the interesting event.
 */
const ONCE_PER_PAGE = new Set(['form_visible', 'wallet_ready']);
const stampedOnce = new Set();

let context = null;

/**
 * Dev signal. Most donor consumers are Vite browser builds where `process` is
 * undefined; Node/vitest contexts use NODE_ENV. Same shape as
 * `utils/subscriptionFeePolicy.js`, deliberately.
 */
function isDevEnvironment() {
  let isDev = false;
  try {
    if (typeof import.meta !== 'undefined' && import.meta.env) {
      isDev = import.meta.env.DEV === true || import.meta.env.MODE === 'development';
    }
  } catch {
    /* import.meta unavailable in this module system — fall through to process.env */
  }
  if (
    !isDev &&
    typeof process !== 'undefined' &&
    process?.env?.NODE_ENV &&
    process.env.NODE_ENV !== 'production'
  ) {
    isDev = true;
  }
  return isDev;
}

function detectInIframe() {
  try {
    return typeof window !== 'undefined' && window.self !== window.top;
  } catch {
    // A cross-origin `window.top` access throws in some engines; that answer is
    // itself "yes, framed".
    return true;
  }
}

/**
 * The embedding page's HOSTNAME, never its URL. `document.referrer` is the only
 * cross-origin-safe source, and its path can carry a campaign slug — so parse
 * and keep the host alone.
 */
function detectHostHostname() {
  try {
    if (typeof document === 'undefined' || !document.referrer) return null;
    return new URL(document.referrer).hostname || null;
  } catch {
    return null;
  }
}

function readConnection() {
  try {
    return navigator?.connection?.effectiveType ?? 'unknown';
  } catch {
    return 'unknown';
  }
}

function readDeviceMemory() {
  try {
    return navigator?.deviceMemory ?? null;
  } catch {
    return null;
  }
}

/** The dimensions SPEC §2 puts on every donor_perf event. */
function dimensions() {
  if (!context) return {};
  const base = {
    surface: context.surface,
    connection: readConnection(),
    device_memory: readDeviceMemory(),
    in_iframe: context.inIframe,
  };
  if (context.hostHostname) base.host_hostname = context.hostHostname;
  // One bundle serves eleven element types (`?entity=` in the elements app);
  // without this a fundraising bar's LCP and a donation form's share one bucket.
  if (context.elementType) base.element_type = context.elementType;
  return base;
}

function nowMs() {
  try {
    return typeof performance !== 'undefined' && typeof performance.now === 'function'
      ? performance.now()
      : 0;
  } catch {
    return 0;
  }
}

function emit(eventName, properties) {
  if (!context) return;
  captureClientEvent(context.posthog, eventName, properties, {
    appName: context.surface,
  });
}

function reportWebVital(metric) {
  emit(WEB_VITAL_EVENT, {
    metric: metric?.name,
    value: metric?.value,
    rating: metric?.rating,
    navigation_type: metric?.navigationType,
    ...dimensions(),
  });
}

function stampPerformanceMark(name) {
  try {
    performance?.mark?.(`${PERFORMANCE_MARK_PREFIX}${name}`);
  } catch {
    // `performance.mark` is best-effort; a failed mark must never reach a donor.
  }
}

/**
 * `true` when this surface is one the caller asked to skip.
 *
 * Evaluated at EMIT time, never at call time, so a mark stamped before
 * `initDonorPerformance` has run is judged against the surface the page turns
 * out to be — there is no race between the idle wiring and an early mark.
 */
function skippedHere(skipSurfaces) {
  return Boolean(context && skipSurfaces && skipSurfaces.includes(context.surface));
}

/** `true` the first time a once-per-page mark is claimed on this page; always `true` for the others. */
function claimOnce(name) {
  if (!ONCE_PER_PAGE.has(name)) return true;
  if (stampedOnce.has(name)) return false;
  stampedOnce.add(name);
  return true;
}

function flushBufferedMarks() {
  const pending = bufferedMarks;
  bufferedMarks = [];
  for (const entry of pending) {
    if (entry.event) {
      // A non-mark event (`markRevealFallback`): no once-per-page claim and no
      // `mark` field — it replays exactly as it would have been emitted.
      emit(entry.event, { ...entry.properties, ...dimensions() });
      continue;
    }
    if (skippedHere(entry.skipSurfaces)) continue;
    if (!claimOnce(entry.mark)) continue;
    emit(JOURNEY_MARK_EVENT, {
      mark: entry.mark,
      elapsed_ms: entry.elapsed_ms,
      ...entry.extra,
      ...dimensions(),
    });
  }
}

/**
 * Register the web-vitals reporters and the PostHog client the marks ride on.
 *
 * Call this AFTER first render, from an idle callback — never before. It is
 * idempotent: a second call on the same page is a no-op.
 *
 * @param {object|null|undefined} posthog  the app's PostHog client
 * @param {object} options
 * @param {string} options.surface         'hosted-page' | 'donation-elements' | 'donation-checkout' | 'portal'
 * @param {boolean} [options.inIframe]     defaults to a `window.self !== window.top` probe
 * @param {string|null} [options.hostHostname] defaults to the referrer hostname when framed
 * @param {string|null} [options.elementType] the elements app's `?entity=` (EMBED_FORM, FUNDRAISING_BAR, …); other surfaces omit it
 * @returns {boolean} true when this call performed the initialisation
 */
export function initDonorPerformance(posthog, { surface, inIframe, hostHostname, elementType } = {}) {
  if (context) return false;
  if (!surface) return false;

  const framed = typeof inIframe === 'boolean' ? inIframe : detectInIframe();
  const host =
    hostHostname === undefined ? (framed ? detectHostHostname() : null) : hostHostname;

  context = {
    posthog,
    surface,
    inIframe: framed,
    hostHostname: host,
    elementType: typeof elementType === 'string' && elementType ? elementType : null,
  };

  const options = { reportAllChanges: false };
  try {
    onLCP(reportWebVital, options);
    onINP(reportWebVital, options);
    onCLS(reportWebVital, options);
    onTTFB(reportWebVital, options);
    onFCP(reportWebVital, options);
  } catch {
    // Measurement must never break a donor surface.
  }

  flushBufferedMarks();
  return true;
}

/**
 * Schedule `initDonorPerformance` off the critical path.
 *
 * This is the wiring call each donor entry file makes, and it exists so that
 * "never block first render" is expressed once rather than copied into four
 * entries: `requestIdleCallback` with a 2 s timeout, falling back to a
 * `setTimeout(…, 0)` where the browser has no idle callback (Safari < 17).
 *
 * @param {object|null|undefined} posthog
 * @param {object} options  same shape as `initDonorPerformance`
 */
export function scheduleDonorPerformance(posthog, options) {
  const run = () => {
    try {
      initDonorPerformance(posthog, options);
    } catch {
      // Measurement must never break a donor surface.
    }
  };

  try {
    if (typeof requestIdleCallback === 'function') {
      requestIdleCallback(run, { timeout: 2000 });
      return;
    }
  } catch {
    /* fall through to the timeout path */
  }
  setTimeout(run, 0);
}

/**
 * Stamp one donor-journey mark: a `performance.mark` plus a PostHog event
 * carrying the elapsed time since `performance.timeOrigin`.
 *
 * Capture only — this must never sit on a code path whose result a donor sees.
 *
 * A mark may legitimately repeat on one page — a donor who retries a declined
 * card produces a second `payment_round_trip`, and that is the interesting one.
 * The once-per-page marks (`ONCE_PER_PAGE`: `form_visible`, `wallet_ready`) are
 * guarded HERE, in the module, not at their anchors — a component ref is per
 * instance, and the anchors remount within one page load. An anchor's own
 * "already fired" ref only saves the call.
 *
 * @param {string} name  one of JOURNEY_MARKS
 * @param {object} [extra]  extra properties (e.g. `duration_ms`, `outcome`, `processor`)
 * @param {object} [options]
 * @param {string[]} [options.skipSurfaces]  surfaces on which this call site is
 *   NOT the owner of the mark, because another anchor in that app stamps it.
 *   A component shared by two apps (`EmbedForm`, which the hosted page also
 *   renders) uses this instead of guessing from the URL.
 */
export function markJourney(name, extra = {}, { skipSurfaces } = {}) {
  if (!KNOWN_MARKS.has(name)) {
    // A typo'd mark silently produces an un-queryable event. Loud in dev,
    // dropped everywhere — and NEVER thrown: four of the call sites sit on the
    // payment path, and the deployed dev stage is built `--mode development`,
    // so a throw here would abort a charge on the environment QA walks.
    if (isDevEnvironment()) {
      try {
        console.error(
          `[donorPerformance] unknown journey mark "${name}". Known marks: ${[...KNOWN_MARKS].join(', ')}`,
        );
      } catch {
        /* console unavailable */
      }
    }
    return;
  }

  stampPerformanceMark(name);
  const elapsed = nowMs();

  if (!context) {
    if (bufferedMarks.length < MAX_BUFFERED_MARKS) {
      bufferedMarks.push({ mark: name, elapsed_ms: elapsed, extra: { ...extra }, skipSurfaces });
    }
    return;
  }

  if (skippedHere(skipSurfaces)) return;
  if (!claimOnce(name)) return;

  emit(JOURNEY_MARK_EVENT, {
    mark: name,
    elapsed_ms: elapsed,
    ...extra,
    ...dimensions(),
  });
}

/**
 * CON-86 / PE-2140 — count a Donate tap the preloaded checkout could not take.
 *
 * The embed script on the nonprofit's page detects this (its checkout iframe
 * errored, or outlived the 6 s bound) but has no telemetry client of its own and
 * is not getting one (`D-no-new-telemetry-vendor`). It posts the incident to the
 * form iframe instead, and this is where that lands.
 *
 * No once-per-page guard: the recovery ladder reports each transition, and
 * collapsing them would hide whether the fresh frame rescued the donor or the
 * navigation did — which is the whole question this event exists to answer.
 *
 * @param {string} reason  one of 'error' | 'deadline' | 'navigated'
 * @param {object} [extra] e.g. `elapsed_ms`
 */
export function markCheckoutTapLost(reason, extra = {}) {
  emit(CHECKOUT_TAP_LOST_EVENT, {
    reason: typeof reason === 'string' && reason ? reason : 'unknown',
    ...extra,
    ...dimensions(),
  });
}

/**
 * CON-73 — count a hosted-page reveal the form's readiness never triggered.
 *
 * Unlike `markCheckoutTapLost` this can run before `initDonorPerformance`: the
 * reveal fallback fires 1.5 s after the content is ready, and the init waits on
 * an idle callback with a 2 s timeout. So a pre-init call is buffered on the
 * same bounded path the journey marks use, and flushed at init. `elapsed_ms` is
 * read at call time, so a buffered event still says when the fallback fired.
 *
 * No once-per-page guard: each firing is its own reveal, and the count is the
 * point.
 *
 * @param {object} [extra] e.g. `form_ready`, `fallback_ms`
 */
export function markRevealFallback(extra = {}) {
  const properties = { elapsed_ms: nowMs(), ...extra };

  if (!context) {
    if (bufferedMarks.length < MAX_BUFFERED_MARKS) {
      bufferedMarks.push({ event: REVEAL_FALLBACK_EVENT, properties });
    }
    return;
  }

  emit(REVEAL_FALLBACK_EVENT, { ...properties, ...dimensions() });
}

/** Test seam. Not exported from the package barrel. */
export function resetDonorPerformanceForTests() {
  context = null;
  bufferedMarks = [];
  stampedOnce.clear();
}
